import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminClientDriveService } from '../services/admin.clientDrive.service'
import type { ClientDriveLink } from '@/types/firebase.types'

type DriveMap = Map<string, ClientDriveLink | null>

export function useAdminClientDrives(clientUids: string[]) {
  const [drives, setDrives] = useState<DriveMap>(new Map())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshTick, setRefreshTick] = useState(0)

  const key = clientUids.slice().sort().join(',')

  useEffect(() => {
    if (clientUids.length === 0) {
      setDrives(new Map())
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    adminClientDriveService
      .getClientUsers(clientUids)
      .then((userMap) => {
        if (cancelled) return
        const next: DriveMap = new Map()
        for (const uid of clientUids) {
          next.set(uid, userMap.get(uid)?.clientDrive ?? null)
        }
        setDrives(next)
      })
      .catch((err) => {
        if (cancelled) return
        setError(formatUserFacingError(err, 'Failed to load drives'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refreshTick])

  const refresh = useCallback(() => setRefreshTick((n) => n + 1), [])

  const provision = useCallback(
    async (uid: string) => {
      const result = await adminClientDriveService.provision(uid)
      refresh()
      return result
    },
    [refresh]
  )

  const link = useCallback(
    async (uid: string, folderUrlOrId: string) => {
      const result = await adminClientDriveService.link(uid, folderUrlOrId)
      refresh()
      return result
    },
    [refresh]
  )

  const unlink = useCallback(
    async (uid: string) => {
      await adminClientDriveService.unlink(uid)
      refresh()
    },
    [refresh]
  )

  return { drives, loading, error, provision, link, unlink, refresh }
}
